/**
 * DisputeTray Component
 * 
 * Persistent sticky bottom bar showing selected items for bulk dispute.
 * Updates live as users select items across different tabs.
 * 
 * SMART ROUTING: Uses Five S classification to route directly to recommended wizard path.
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ClipboardCheck,
  X,
  ChevronUp,
  ChevronDown,
  Trash2,
  Gavel,
  CreditCard,
  Search,
  FileSearch,
  User,
  Building,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { useDisputeTray, DISPUTE_CATEGORIES, CATEGORY_LABELS } from '../../context/DisputeTrayContext';
import { toast } from 'sonner';

// Category icons
const CATEGORY_ICONS = {
  [DISPUTE_CATEGORIES.ACCOUNTS]: CreditCard,
  [DISPUTE_CATEGORIES.INQUIRIES]: Search,
  [DISPUTE_CATEGORIES.PUBLIC_RECORDS]: FileSearch,
  [DISPUTE_CATEGORIES.PERSONAL_INFO]: User,
  [DISPUTE_CATEGORIES.CREDITOR_CONTACTS]: Building
};

// Category colors
const CATEGORY_COLORS = {
  [DISPUTE_CATEGORIES.ACCOUNTS]: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  [DISPUTE_CATEGORIES.INQUIRIES]: 'bg-amber-100 text-amber-700 border-amber-200',
  [DISPUTE_CATEGORIES.PUBLIC_RECORDS]: 'bg-red-100 text-red-700 border-red-200',
  [DISPUTE_CATEGORIES.PERSONAL_INFO]: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  [DISPUTE_CATEGORIES.CREDITOR_CONTACTS]: 'bg-amber-100 text-amber-700 border-amber-200'
};

/**
 * Get display name for an item based on its category
 */
const getItemDisplayName = (item, category) => {
  switch (category) {
    case DISPUTE_CATEGORIES.ACCOUNTS:
      return item.account_name || item.creditor_name || 'Unknown Account';
    case DISPUTE_CATEGORIES.INQUIRIES:
      return item.company_name || item.creditor || 'Unknown Inquiry';
    case DISPUTE_CATEGORIES.PUBLIC_RECORDS:
      return (item.type || item.record_type || 'Public Record').replace(/_/g, ' ');
    case DISPUTE_CATEGORIES.PERSONAL_INFO:
      return item.field || item.label || 'Personal Info';
    case DISPUTE_CATEGORIES.CREDITOR_CONTACTS:
      return item.name || item.raw_name || 'Creditor Contact';
    default:
      return 'Unknown Item';
  }
};

/**
 * Get item ID based on category
 */
const getItemId = (item) => {
  return item.tradeline_id || item.inquiry_id || item.record_id || item.id || item.field;
};

/**
 * Category Group Component
 */
const CategoryGroup = ({ category, items, onRemoveItem }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = CATEGORY_ICONS[category];
  const colorClass = CATEGORY_COLORS[category];
  
  if (items.length === 0) return null;
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', colorClass.split(' ')[0])}>
            <Icon size={16} className={colorClass.split(' ')[1]} />
          </div>
          <span className="font-medium text-stone-800">{CATEGORY_LABELS[category]}</span>
          <Badge variant="secondary" className="ml-2">{items.length}</Badge>
        </div>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      
      {isExpanded && (
        <div className="p-2 space-y-1 max-h-40 overflow-y-auto bg-white">
          {items.map((item) => {
            const itemId = getItemId(item);
            return (
              <div 
                key={itemId}
                className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 text-sm"
              >
                <span className="text-slate-700 truncate flex-1">
                  {getItemDisplayName(item, category)}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveItem(category, itemId);
                  }}
                  className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Main DisputeTray Component
 */
export const DisputeTray = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedItems,
    totalCount,
    categoryCounts,
    removeItem,
    clearAll,
    isTrayExpanded,
    setIsTrayExpanded,
    startSmartDispute,
    isClassifying,
  } = useDisputeTray();
  
  // ROUTE-BASED VISIBILITY: Hide the tray inside the Dispute Wizard
  // The wizard owns its own selection state via navigation state
  const isInsideDisputeWizard = location.pathname.startsWith('/dispute-wizard');
  
  // Don't render if inside Dispute Wizard (wizard owns the state now)
  if (isInsideDisputeWizard) return null;
  
  // Don't render if nothing selected
  if (totalCount === 0) return null;
  
  // Get active categories (those with items)
  const activeCategories = Object.entries(categoryCounts)
    .filter(([_, count]) => count > 0)
    .map(([category]) => category);
  
  /**
   * Handle "Create Dispute Package" click
   * Uses FIVE S classification to route directly to recommended wizard path
   */
  const handleCreateDisputePackage = async () => {
    try {
      const result = await startSmartDispute();
      
      if (!result) {
        toast.error('Please select at least one item to dispute');
        return;
      }
      
      // Show classification result
      const strategyDisplay = result.strategyTitle || result.classification || 'Dispute';
      toast.success(`Strategy: ${strategyDisplay}`, {
        description: 'Routing to recommended dispute path...',
        duration: 2000,
      });
      
      // Navigate directly to the wizard with preloaded data
      navigate(result.route, { state: result.state });
      
      // Note: Don't clear the tray here - let the wizard handle it after completion
      
    } catch (error) {
      console.error('Smart dispute routing error:', error);
      toast.error('Failed to classify dispute. Please try again.');
    }
  };
  
  return (
    <div 
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50 transition-all duration-300',
        'bg-white border-t border-slate-200 shadow-2xl'
      )}
      data-testid="dispute-tray"
    >
      {/* Expanded View */}
      {isTrayExpanded && (
        <div className="max-h-[50vh] overflow-y-auto p-4 border-b border-slate-100">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Selected Items for Dispute
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsTrayExpanded(false)}
              >
                <ChevronDown size={16} className="mr-1" />
                Collapse
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {activeCategories.map(category => (
                <CategoryGroup
                  key={category}
                  category={category}
                  items={selectedItems[category]}
                  onRemoveItem={removeItem}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Collapsed Bar */}
      <div className="p-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          {/* Left: Tray Info */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <ClipboardCheck size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-stone-800">
                  {totalCount} item{totalCount !== 1 ? 's' : ''} selected
                </p>
                <p className="text-xs text-slate-500">
                  Ready for review • {activeCategories.length} categor{activeCategories.length !== 1 ? 'ies' : 'y'}
                </p>
              </div>
            </div>
            
            {/* Category Badges */}
            <div className="hidden sm:flex items-center gap-2 flex-wrap">
              {activeCategories.map(category => {
                const Icon = CATEGORY_ICONS[category];
                const count = categoryCounts[category];
                return (
                  <div 
                    key={category}
                    className={cn(
                      'flex items-center gap-1 px-2 py-1 rounded-full text-xs border',
                      CATEGORY_COLORS[category]
                    )}
                  >
                    <Icon size={12} />
                    <span>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsTrayExpanded(!isTrayExpanded)}
              className="text-slate-600"
              data-testid="expand-tray"
            >
              {isTrayExpanded ? (
                <>
                  <ChevronDown size={16} className="mr-1" />
                  <span className="hidden sm:inline">Hide</span>
                </>
              ) : (
                <>
                  <ChevronUp size={16} className="mr-1" />
                  <span className="hidden sm:inline">Review</span>
                </>
              )}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="clear-tray"
            >
              <Trash2 size={16} className="mr-1" />
              <span className="hidden sm:inline">Clear</span>
            </Button>
            
            <Button
              onClick={handleCreateDisputePackage}
              disabled={isClassifying}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="create-dispute-package"
            >
              {isClassifying ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Classifying...
                </>
              ) : (
                <>
                  <Sparkles size={16} className="mr-2" />
                  Create Dispute Package
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisputeTray;
